import React, { useMemo } from 'react';
import { withRef } from '@udecode/cn';
import {
  isSelectionExpanded,
  useEditorRef,
  useEditorSelector,
  useElement,
  useRemoveNodeButton,
  withHOC,
  toDOMNode,
} from '@udecode/plate-common';
import {
  TableProvider,
  mergeTableCells,
  unmergeTableCells,
  insertTableColumn,
  insertTableRow,
  deleteRow,
  deleteColumn,
  useTableBordersDropdownMenuContentState,
  useTableElement,
  useTableElementState,
  useTableMergeState,
  useTableStore,
} from '@udecode/plate-table';
import { useReadOnly, useSelected } from 'slate-react';
import {
  offset,
  flip,
} from '@floating-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '../../components/dropdown-menu/dropdown-menu';
import { Popover } from '../../components/popup/Floating/ElementPopover';
import { getCurrentCellData } from './utils/getCurrentCellData';
import { PlateTableBackgroundPicker } from './PlateTableBackgroundPicker';

export const TableBordersDropdownMenuContent = withRef((props, ref) => {
  const {
    getOnSelectTableBorder,
    hasBottomBorder,
    hasLeftBorder,
    hasNoBorders,
    hasOuterBorders,
    hasRightBorder,
    hasTopBorder,
  } = useTableBordersDropdownMenuContentState();

  return (
    <DropdownMenuContent
      align="start"
      className="slate-popover slate-popover--table"
      ref={ref}
      side="right"
      sideOffset={0}
      style={{ zIndex: 40 }}
      {...props}
    >
      <>
        <DropdownMenuCheckboxItem
          checked={hasBottomBorder}
          onCheckedChange={getOnSelectTableBorder('bottom')}
        >
          <FontAwesomeIcon icon={['fal', 'border-bottom']} />
          <div>Bottom Border</div>
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={hasTopBorder}
          onCheckedChange={getOnSelectTableBorder('top')}
        >
          <FontAwesomeIcon icon={['fal', 'border-top']} />
          <div>Top Border</div>
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={hasLeftBorder}
          onCheckedChange={getOnSelectTableBorder('left')}
        >
          <FontAwesomeIcon icon={['fal', 'border-left']} />
          <div>Left Border</div>
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={hasRightBorder}
          onCheckedChange={getOnSelectTableBorder('right')}
        >
          <FontAwesomeIcon icon={['fal', 'border-right']} />
          <div>Right Border</div>
        </DropdownMenuCheckboxItem>

        <div className="menu-separator" />

        <DropdownMenuCheckboxItem
          checked={hasNoBorders}
          onCheckedChange={getOnSelectTableBorder('none')}
        >
          <FontAwesomeIcon icon={['fal', 'border-none']} />
          <div>No Border</div>
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={hasOuterBorders}
          onCheckedChange={getOnSelectTableBorder('outer')}
        >
          <FontAwesomeIcon icon={['fal', 'border-outer']} />
          <div>Outside Borders</div>
        </DropdownMenuCheckboxItem>
      </>
    </DropdownMenuContent>
  );
});

export const TableInsertDropdownMenuContent = withRef((props, ref) => {
  const { editor } = props;

  return (
    <DropdownMenuContent
      align="start"
      className="slate-popover slate-popover--table"
      ref={ref}
      side="right"
      sideOffset={0}
      style={{ zIndex: 40 }}
      {...props}
    >
      <>
        {/* Removing for now to avoid having to manually get path
        <button
          contentEditable={false}
          onClick={() => insertTableRow(editor)}
          className="dropdown-button--right button--popover button--unstyled"
        >
          Insert row above
        </button>
        */}
        <button
          contentEditable={false}
          onClick={() => {
            insertTableRow(editor);
          }}
          className="dropdown-button--right button--popover button--unstyled"
        >
          Insert row below
        </button>
        {/* Removing for now to avoid having to manually get path
        <button
          contentEditable={false}
          onClick={() => insertTableColumn(editor)}
          className="dropdown-button--right button--popover button--unstyled"
        >
          Insert column left
        </button>
        */}
        <button
          contentEditable={false}
          onClick={() => insertTableColumn(editor)}
          className="dropdown-button--right button--popover button--unstyled"
        >
          Insert column right
        </button>
      </>
    </DropdownMenuContent>
  );
});

export const TableDeleteDropdownMenuContent = withRef((props, ref) => {
  const element = useElement();
  const { editor } = props;
  const { props: buttonProps } = useRemoveNodeButton({ element });

  return (
    <DropdownMenuContent
      align="start"
      className="slate-popover slate-popover--table"
      ref={ref}
      side="right"
      sideOffset={0}
      style={{ zIndex: 40 }}
      {...props}
    >
      <>
        <button
          contentEditable={false}
          onClick={() => deleteRow(editor)}
          className="dropdown-button--right button--popover button--unstyled"
        >
          Delete Row
        </button>
        <button
          contentEditable={false}
          onClick={() => deleteColumn(editor)}
          className="dropdown-button--right button--popover button--unstyled"
        >
          Delete Column
        </button>
        <button
          contentEditable={false}
          {...buttonProps}
          className="dropdown-button--right button--popover button--unstyled"
        >
          Delete Table
        </button>
      </>
    </DropdownMenuContent>
  );
});

export const TableFloatingToolbar = withRef(
  ({ children, ...props }, ref) => {

    const selectionCollapsed = useEditorSelector(
      (editor) => !isSelectionExpanded(editor),
      [],
    );

    const readOnly = useReadOnly();
    const selected = useSelected();
    const editor = useEditorRef();
    const selectedCells = useTableStore().get.selectedCells();

    const collapsed = !readOnly && selected && selectionCollapsed;
    const open = !readOnly && selected;

    const domNode = useMemo(() => {
      const { currentCell } = getCurrentCellData(editor, selectedCells);
      if (!selectedCells && !currentCell) return;

      if (!selectedCells) return toDOMNode(editor, currentCell[0]);

      // We will use the last item in the selection to shortcut doing more
      // complex calculating involving a virtual element of everything selected.
      const cell = selectedCells[selectedCells.length -1];
      if (cell) {
        return toDOMNode(editor, cell);
      }
    }, [editor.selection]);

    const floatingOptions = {
      middleware: [
        offset(12),
        flip(),
      ],
      ... domNode && {
        elements: {
          reference: domNode,
        },
      },
      open,
    };

    const { canMerge, canUnmerge } = useTableMergeState();

    const mergeContent = canMerge && (
      <button
        contentEditable={false}
        onClick={() => mergeTableCells(editor)}
        className="button--popover button--unstyled"
      >
        Merge
      </button>
    );

    const unmergeButton = canUnmerge && (
      <button
        contentEditable={false}
        onClick={() => unmergeTableCells(editor)}
        className="button--popover button--unstyled"
      >
        Unmerge
      </button>
    );

    const bordersContent = collapsed && (
      <>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              className="dropdown-button--right button--popover button--unstyled"
            >
              <div>Borders</div>
              <FontAwesomeIcon
                icon={['fal', 'chevron-right']}
                size="xs"
              />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuPortal>
            <TableBordersDropdownMenuContent />
          </DropdownMenuPortal>
        </DropdownMenu>
      </>
    );

    const backgroundContent = (
      <>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              className="dropdown-button--right button--popover button--unstyled"
            >
              <div>Cell Background</div>
              <FontAwesomeIcon
                icon={['fal', 'chevron-right']}
                size="xs"
              />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuPortal>
            <PlateTableBackgroundPicker />
          </DropdownMenuPortal>
        </DropdownMenu>
      </>
    );

    const insertContent = (
      <>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              className="dropdown-button--right button--popover button--unstyled"
            >
              <div>Insert</div>
              <FontAwesomeIcon
                icon={['fal', 'chevron-right']}
                size="xs"
              />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuPortal>
            <TableInsertDropdownMenuContent
              editor={editor}
            />
          </DropdownMenuPortal>
        </DropdownMenu>
      </>
    );

    const deleteContent = collapsed && (
      <>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              className="dropdown-button--right button--popover button--unstyled"
            >
              <div>Delete</div>
              <FontAwesomeIcon
                icon={['fal', 'chevron-right']}
                size="xs"
              />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuPortal>
            <TableDeleteDropdownMenuContent editor={editor} />
          </DropdownMenuPortal>
        </DropdownMenu>
      </>
    );

    return (
      <Popover
        ref={ref}
        className="slate-popover slate-popover--table"
        floatingOptions={floatingOptions}
        content={(
          <>
            {unmergeButton}
            {mergeContent}
            {bordersContent}
            {backgroundContent}
            {insertContent}
            {deleteContent}
          </>
        )}
        {...props}
      >
        {children}
      </Popover>
    );
  },
);

export const TableElement = withHOC(
  TableProvider,
  withRef(({ children, className, ...props }, ref) => {
    const { attributes } = props;
    const { colSizes, marginLeft, minColumnWidth } =
      useTableElementState();
    const { colGroupProps, props: tableProps } = useTableElement();

    return (
      <TableFloatingToolbar>
        <div style={{ paddingLeft: marginLeft, maxWidth: '100%' }}>
          <table
            className={className}
            ref={ref}
            {...tableProps}
            {...attributes}
          >
            <colgroup {...colGroupProps}>
              {colSizes.map((width, index) => (
                <col
                  key={index}
                  style={{
                    minWidth: minColumnWidth,
                    width: width || undefined,
                  }}
                />
              ))}
            </colgroup>

            <tbody>{children}</tbody>
          </table>
        </div>
      </TableFloatingToolbar>
    );
  }),
);
